import React from "react";
import { FiArrowLeft, FiBarChart2 } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import CoinIcon from "../components/CoinIcon";
import { useBinanceTicker } from "../services/BinanceTickerContext";
import { marketAsset, marketChange, marketName, marketNetworks, marketNumber, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";

const displayNumber = (value) => {
  const number = marketNumber(value);
  return number == null ? "—" : number.toLocaleString("en-US");
};

function MarketAssetDetailLoading() {
  return <section className="market-asset-detail asset-detail-loading" aria-busy="true" aria-label="Loading asset details">
    <p className="market-loading-status" role="status">Loading asset details</p>
    <div className="market-skeleton asset-detail-loading-back" aria-hidden="true" />
    <div className="asset-detail-heading" aria-hidden="true">
      <i className="market-skeleton asset-detail-loading-icon" />
      <div className="asset-detail-loading-title"><i className="market-skeleton" /><i className="market-skeleton" /></div>
      <i className="market-skeleton asset-detail-loading-trade" />
    </div>
    <div className="asset-detail-price" aria-hidden="true">
      <i className="market-skeleton asset-detail-loading-price" />
      <i className="market-skeleton asset-detail-loading-change" />
    </div>
    <div className="asset-detail-grid" aria-hidden="true">
      {Array.from({ length: 9 }, (_, index) => <div key={index}>
        <i className="market-skeleton asset-detail-loading-label" />
        <i className="market-skeleton asset-detail-loading-value" />
      </div>)}
    </div>
    <section className="asset-network-section" aria-hidden="true">
      <h2>Supported Networks</h2>
      {[0, 1, 2].map((index) => <div className="asset-detail-loading-network" key={index}>
        <span><i className="market-skeleton" /><i className="market-skeleton" /></span>
        <i className="market-skeleton" />
      </div>)}
    </section>
  </section>;
}

function MarketAssetDetail() {
  const { symbol: routeSymbol } = useParams();
  const symbol = decodeURIComponent(routeSymbol ?? "").toUpperCase();
  const { assets, loading, error } = useMarketAssets();
  const market = assets.find((row) => marketSymbol(row) === symbol);
  const asset = marketAsset(market);
  const ticker = useBinanceTicker(symbol);
  const change = marketChange(ticker);
  const networks = marketNetworks(market);

  if (loading) return <MarketAssetDetailLoading />;
  if (error) return <p className="market-state market-state-error" role="alert">{error}</p>;
  if (!market) return <p className="market-state">This asset is not an active market.</p>;

  return (
    <section className="market-asset-detail">
      <Link className="market-back-link" to="/markets/overview"><FiArrowLeft aria-hidden="true" /> Markets overview</Link>
      <div className="asset-detail-heading">
        <CoinIcon size="large" symbol={symbol} logo={asset.logo} name={marketName(market)} />
        <div><h1>{marketName(market)} <span>{symbol}</span></h1><p>{market.market_symbol ?? market.symbol}</p></div>
        <Link className="asset-trade-link" to={`/trade?symbol=${encodeURIComponent(market.market_symbol ?? market.symbol ?? `${symbol}USDT`)}`}><FiBarChart2 aria-hidden="true" /> Trade</Link>
      </div>
      <div className="asset-detail-price">
        <strong>{marketPrice(ticker)}</strong>
        <span className={change.startsWith("+") ? "up" : change === "—" ? "" : "down"}>{change}</span>
      </div>
      <div className="asset-detail-grid">
        <div><small>24h High</small><b>{ticker?.high == null ? "—" : `$${ticker.high.toLocaleString("en-US")}`}</b></div>
        <div><small>24h Low</small><b>{ticker?.low == null ? "—" : `$${ticker.low.toLocaleString("en-US")}`}</b></div>
        <div><small>24h Volume</small><b>{ticker?.volume == null ? "—" : ticker.volume.toLocaleString("en-US")}</b></div>
        <div><small>Category</small><b>{market.category || "—"}</b></div>
        <div><small>Contract Address</small><b>{asset.address || "—"}</b></div>
        <div><small>Total Supply</small><b>{displayNumber(market.total_supply)}</b></div>
        <div><small>Total Unlocked</small><b>{displayNumber(market.total_unlocked)}</b></div>
        <div><small>Next Unlock</small><b>{displayNumber(market.next_unlock_amount)}</b></div>
        <div><small>Next Unlock Date</small><b>{market.next_unlock_at ? new Date(market.next_unlock_at).toLocaleString() : "—"}</b></div>
      </div>
      <section className="asset-network-section">
        <h2>Supported Networks</h2>
        {networks.length ? networks.map((network) => <div key={network.id ?? network.symbol ?? network.name} className="asset-network-row"><span>{network.name ?? network.symbol}<small>{network.chain_id ? `Chain ${network.chain_id}` : ""}</small></span>{network.explorer_url ? <a href={network.explorer_url} target="_blank" rel="noreferrer">Explorer</a> : <span>{network.symbol ?? ""}</span>}</div>) : <p>No active network is configured for this asset.</p>}
      </section>
    </section>
  );
}

export default MarketAssetDetail;